// shell/Topbar/NotificationBell.tsx — topbar bell + unread count badge + unread-only peek dropdown.

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton, Popover } from '@/ui';
import { useNotifications } from '@/hooks';
import type { Notification } from '@/core/models/notification';
import { networkNotificationPath } from '@/core/models/notification';
import { NotificationCard } from '@/features/notifications/NotificationCard';
import { CountBadge } from '../CountBadge';

const BadgeWrap = styled.div`
  position: relative;
  display: inline-flex;
  & > span:last-child:not(:first-child) {
    position: absolute; top: -2px; right: -4px; pointer-events: none;
  }
`;

const Panel = styled.div`
  width: 320px;
  max-height: 420px;
  overflow-y: auto;
`;

const PeekHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const PeekTitle = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TextBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover { color: ${({ theme }) => theme.color.textPrimary}; }
`;

const SeeAll = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover { color: ${({ theme }) => theme.color.textPrimary}; }
`;

const CaughtUp = styled.div`
  padding: 20px 16px;
  text-align: left;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const unread = notifications.filter((n) => !n.read);

  function activate(n: Notification) {
    void markRead(n.id);
    setOpen(false);
    const networkPath = networkNotificationPath(n);
    if (networkPath) { navigate(networkPath); return; }
    // BARE on purpose (1.24.50). A notification carries `actor.handle` — the
    // person who liked/replied — which is NOT the post's author, so naming the
    // address from it would mint a deliberately wrong handle. The detail page
    // relabels to the real author on arrival, which is exactly what that relabel
    // is for.
    if (n.postId) navigate(`/post/${encodeURIComponent(n.postId)}${n.commentId ? `?comment=${encodeURIComponent(n.commentId)}` : ''}`);
    else if (n.type === 'council') navigate('/council');
  }

  return (
    <BadgeWrap>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger={<IconButton icon="bell" label="Notifications" />}
      >
        <Panel>
          <PeekHead>
            <PeekTitle>Notifications</PeekTitle>
            <Actions>
              <TextBtn type="button" onClick={() => void markAllRead()} disabled={unreadCount === 0}>
                Mark all as read
              </TextBtn>
              <SeeAll to="/notifications" onClick={() => setOpen(false)}>See All</SeeAll>
            </Actions>
          </PeekHead>
          {unread.length === 0 ? (
            <CaughtUp>You&apos;re all caught up</CaughtUp>
          ) : (
            unread.map((n) => (
              <NotificationCard key={n.id} notification={n} onActivate={activate} compact />
            ))
          )}
        </Panel>
      </Popover>
      {unreadCount > 0 && <CountBadge>{unreadCount}</CountBadge>}
    </BadgeWrap>
  );
}
